/**
 * LocationSharingButton - UI component for sharing location in P2P chat
 */

import { useState, useRef, useEffect } from 'react'
import { MapPin, Navigation, Clock, X } from 'lucide-react'
import { locationService } from '../../services/LocationService'
import { toast } from '../../utils/toastManager'
import { LocationSharingModal } from './LocationSharingModal'

interface LocationSharingButtonProps {
  peerId: string
  peerAlias: string
  onLocationShare: (location: any, shareType: 'one-time' | 'live' | 'duration', duration?: number) => void
  isSharing?: boolean
}

export function LocationSharingButton({
  peerId,
  peerAlias,
  onLocationShare,
  isSharing = false
}: LocationSharingButtonProps) {
  const [showMenu, setShowMenu] = useState(false)
  const [showMobileModal, setShowMobileModal] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)
  const [shareType, setShareType] = useState<'one-time' | 'live' | 'duration'>('one-time')
  const [durationMinutes, setDurationMinutes] = useState(15)
  const [isMobile, setIsMobile] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Detect mobile device
    const checkMobile = () => {
      const mobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                    window.innerWidth < 768
      setIsMobile(mobile)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleShareLocation = async (type: 'one-time' | 'live' | 'duration') => {
    setIsRequesting(true)

    try {
      // Check permission first to provide better user feedback
      const permissionStatus = await locationService.checkPermission()

      if (permissionStatus === 'unsupported') {
        toast.error('Location sharing is not supported in this browser')
        return
      }

      if (permissionStatus === 'denied') {
        toast.error('Location permission denied. Please enable location access in your browser settings.')
        return
      }

      if (type === 'one-time') {
        const location = await locationService.getCurrentLocation()
        const accuracyLevel = locationService.getAccuracyLevelForPeer(peerId)
        const processedLocation = locationService.applyPrivacyLevel(location, accuracyLevel)
        onLocationShare(processedLocation, 'one-time')
        toast.success(`Location shared with ${peerAlias}`)
      } else if (type === 'live') {
        locationService.startLiveTracking(peerId)
        onLocationShare(null, 'live')
        toast.success(`Started live location sharing with ${peerAlias}`)
      } else if (type === 'duration') {
        locationService.startDurationSharing(peerId, durationMinutes)
        onLocationShare(null, 'duration', durationMinutes)
        toast.success(`Sharing location with ${peerAlias} for ${durationMinutes} minutes`)
      }

      setShowMenu(false)
    } catch (error: any) {
      if (error.code === 1) {
        toast.error('Location permission denied. Please enable location access in your browser settings.')
      } else if (error.code === 2) {
        toast.error('Unable to get your location. Please check your device settings.')
      } else if (error.code === 3) {
        toast.error('Location request timed out. Please try again.')
      } else {
        toast.error('Failed to share location: ' + (error.message || 'Unknown error'))
      }
      console.error('Location sharing error:', error)
    } finally {
      setIsRequesting(false)
    }
  }

  const handleStopSharing = () => {
    locationService.stopLiveTracking(peerId)
    toast.success('Stopped location sharing')
    setShowMenu(false)
  }

  const handleButtonClick = () => {
    if (isMobile) {
      setShowMobileModal(true)
    } else {
      setShowMenu(!showMenu)
    }
  }

  if (isSharing) {
    return (
      <button
        onClick={handleStopSharing}
        className="p-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors animate-pulse"
        title="Stop sharing location"
      >
        <div className="flex items-center gap-1">
          <Navigation className="h-5 w-5" />
          <X className="h-3 w-3" />
        </div>
      </button>
    )
  }

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={handleButtonClick}
          disabled={isRequesting}
          className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          title="Share location"
        >
          {isRequesting ? (
            <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full" />
          ) : (
            <MapPin className="h-5 w-5" />
          )}
        </button>

      {showMenu && (
        <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[200px] z-10">
          <div className="space-y-1">
            <button
              onClick={() => handleShareLocation('one-time')}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
            >
              <MapPin className="h-4 w-4" />
              <span className="text-sm">Share current location</span>
            </button>

            <button
              onClick={() => handleShareLocation('live')}
              className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
            >
              <Navigation className="h-4 w-4" />
              <span className="text-sm">Share live location</span>
            </button>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-1 mt-1">
              <button
                onClick={() => setShareType('duration')}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
              >
                <Clock className="h-4 w-4" />
                <span className="text-sm">Share for duration</span>
              </button>

              {shareType === 'duration' && (
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="5"
                      max="480"
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 15)}
                      className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">minutes</span>
                    <button
                      onClick={() => handleShareLocation('duration')}
                      className="ml-auto px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Share
                    </button>
                  </div>
                  <div className="mt-1 flex gap-1">
                    {[15, 30, 60].map(minutes => (
                      <button
                        key={minutes}
                        onClick={() => setDurationMinutes(minutes)}
                        className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        {minutes}m
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Mobile Modal */}
      <LocationSharingModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        peerId={peerId}
        peerAlias={peerAlias}
        onLocationShare={onLocationShare}
      />
    </>
  )
}